import {
  Component, EventEmitter, OnInit, Input, Output, QueryList,
  ViewChild, ViewChildren, ChangeDetectorRef, OnChanges, SimpleChanges, ElementRef, Renderer2
} from '@angular/core';
import { NgForm, AbstractControl } from '@angular/forms';
import { Subscription, of, pipe } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { mockDataOptions } from '@app/mocks/data/mock_touchpoints';
import { EudsSelectComponent, EudsTextareaComponent, EudsTextfieldComponent, EudsAutocompleteComponent } from '@eucoms/common-components/design-system';
import { AppConfigService, OpCo } from '@eucoms/common-components';
import {
  CustomerTouchPoint, InteractionChannelObject, OpCoObject,
  OpCo as CustTpOpCo, TouchPointStatusObject, TouchPointTypeObject, JourneyDefinition, JourneyStepDefinition
} from '@eucoms/customer-touchpoints-tracker';
import { ApiService } from '@app/services/api.service';
import { ProductWithDefinition } from '@app/model/products';
import { MsalService } from '@azure/msal-angular';

@Component({
  selector: 'app-touchpoint-form',
  templateUrl: './touchpoint-form.component.html',
  styleUrls: ['./touchpoint-form.component.scss'],
})
export class TouchpointFormComponent implements OnInit, OnChanges {
  @ViewChild('touchpointForm') touchpointForm: NgForm;
  @ViewChildren(EudsTextfieldComponent) textfieldComponents: QueryList<EudsTextfieldComponent>;
  @ViewChildren(EudsTextareaComponent) textareaComponents: QueryList<EudsTextareaComponent>;
  @ViewChildren(EudsSelectComponent) selectComponents: QueryList<EudsSelectComponent>;
  @ViewChild('opcoSelectControlRef', { read: ElementRef }) opcoSelectElementRef: ElementRef;
  @ViewChild('autocompleteProduct') autocompleteProduct: EudsAutocompleteComponent;
  @ViewChild('autocompleteJourney') autocompleteJourney: EudsAutocompleteComponent;
  @ViewChild('autocompleteStep') autocompleteStep: EudsAutocompleteComponent;

  @Input() opCoSelected: OpCo;
  @Input() opcoFieldError = false;

  @Output() formSubmitted = new EventEmitter<{ valid: boolean; data: CustomerTouchPoint | null }>();

  mockDataOptions = mockDataOptions;
  selectedMockData: any;
  opcoOptions = Object.values(OpCo);
  timestampDateOverride: any = null;

  // Update formModel to match CustomerTouchPoint interface
  formModel: CustomerTouchPoint = {
    accountNumber: '',
    opCo: OpCoObject.ACE,
    status: TouchPointStatusObject.Unknown,
    type: TouchPointTypeObject.Unknown,
    channel: InteractionChannelObject.Web,
    timestamp: new Date(),
    customerInfo: {
      phoneNumber: '',
      customerNumber: '',
      premiseIds: ['']
    },
    processingContext: {
      product: '',
      journey: '',
      step: '',
      resultCode: '',
      resultDescription: '',
      extensionData: null
    },
    dependency: {
      service: '',
      operation: '',
      endpoint: '',
      httpStatusCode: undefined // Use number | undefined
    },
    sessionId: '',
    target: '',
    additionalInfo: null
  };

  // Add Enums for dropdowns
  interactionChannelOptions = Object.values(InteractionChannelObject);
  touchPointStatusOptions = Object.values(TouchPointStatusObject);
  touchPointTypeOptions = Object.values(TouchPointTypeObject);

  // Product definitions fetched from the API
  productDefinitions: ProductWithDefinition[] = [];

  // The options to show in the product autocomplete
  optionsProduct: any[] = [];

  // The journeys from the selected product
  productJourneys: JourneyDefinition[] = [];

  // The options to show in the journey autocomplete
  optionsJourney: any[] = [];

  // The steps from the selected journey
  journeySteps: JourneyStepDefinition[] = [];

  // The options to show in the step autocomplete
  optionsStep: any[] = [];

  copyStatusMessage: string = '';
  private copyMessageTimeout: any = null;

  // Validation message for account number
  accountNumberValidationError: string = 'Account number must be 10 numeric characters.';
  // Length for account number
  accountNumberLength: number = 10;

  constructor(
    private appConfig: AppConfigService,
    private cdRef: ChangeDetectorRef,
    private renderer: Renderer2,
    private apiService: ApiService,
    private authService: MsalService,
  ) { }

  ngOnInit(): void {
    this.formModel.opCo = null;
    this.opCoSelected ??= this.appConfig.config.opCo;
    this.formModel.opCo = this.opCoSelected as CustTpOpCo;

    const opcoString = this.formModel.opCo?.toString().toLowerCase(); // Ensure lowercase comparison
    // Determine length based on OpCo
    this.accountNumberLength = ['ace', 'dpl', 'pepco'].includes(opcoString) ? 11 : 10;
    // Update validation message for account number
    this.accountNumberValidationError = 'Account number for ' + this.formModel.opCo + ' must be ' + this.accountNumberLength + ' numeric characters';

    if (this.selectedMockData) {
      this.prefillForm();
    }
    const now = new Date();
    this.formModel.timestamp = now;

    // Set the date picker to show today's date (proper IMyDateModel format for EUDS)
    this.timestampDateOverride = this.createIMyDateModel(now);

    // Fetch product definitions
    this.fetchProductDefinitions();
  }

  get isOpcoInvalid(): boolean {
    const ctrl = this.touchpointForm?.form?.controls['opco'];
    return this.opcoFieldError || (ctrl?.invalid && ctrl?.touched);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.opcoFieldError && this.touchpointForm?.form) {
      const opcoCtrl = this.touchpointForm.form.get('opco');
      if (opcoCtrl) {
        if (this.opcoFieldError) {
          opcoCtrl.setErrors({ mismatch: true });
          opcoCtrl.markAsTouched();

          // Need this to style Select for our type of Error
          this.setInternalSelectErrorClass(this.opcoSelectElementRef, true);

          console.log('OpCo field error (API mismatch): Control set to invalid and touched. Attempted style override.');
        } else {
          // Logic to clear the error if opcoFieldError is set back to false by parent
          if (opcoCtrl.hasError('mismatch')) {
            delete opcoCtrl.errors['mismatch'];
            if (Object.keys(opcoCtrl.errors || {}).length === 0) {
              opcoCtrl.setErrors(null);
            }
          }
          this.setInternalSelectErrorClass(this.opcoSelectElementRef, false);
        }
        this.cdRef.detectChanges();
      }
    }
  }

  private setInternalSelectErrorClass(selectElementRef: ElementRef, showError: boolean): void {
    const errorClasses = ['btn-selector-error', 'error'];

    // safety check
    if (!selectElementRef?.nativeElement) {
      return;
    }

    const hostEl = selectElementRef.nativeElement as HTMLElement;

    let btn: HTMLElement | null = null;
    if (hostEl.shadowRoot) {
      btn = hostEl.shadowRoot.querySelector('button#select-button.btn-selector');
    } else {
      btn = hostEl.querySelector('button#select-button.btn-selector');
    }

    if (!btn) {
      return;
    }

    // toggle each class
    errorClasses.forEach(cls => {
      if (showError) {
        this.renderer.addClass(btn!, cls);
      } else {
        this.renderer.removeClass(btn!, cls);
      }
    });
  }

  get opcoFormControl(): AbstractControl | null {
    return this.touchpointForm?.form?.get('opco');
  }

  addPremiseId(): void {
    if (!this.formModel.customerInfo.premiseIds) {
      this.formModel.customerInfo.premiseIds = [];
    }
    this.formModel.customerInfo.premiseIds.push('');
  }

  prefillForm(): void {
    if (this.selectedMockData) {
      const mock = this.selectedMockData.data as Partial<CustomerTouchPoint>;

      // Merge top-level properties
      this.formModel = {
        ...this.formModel, // Keep default values for missing fields
        ...mock,
        // Deep merge nested objects manually
        customerInfo: {
          ...this.formModel.customerInfo,
          ...(mock.customerInfo || {}),
          premiseIds: mock.customerInfo?.premiseIds
            ? [...mock.customerInfo.premiseIds]
            : [''] // Ensure premiseIds is an array
        },
        processingContext: {
          ...this.formModel.processingContext,
          ...(mock.processingContext || {}),
          extensionData: (mock.processingContext?.extensionData
            ? JSON.stringify(mock.processingContext.extensionData, null, 2)
            : null) as any
        },
        dependency: {
          ...this.formModel.dependency,
          ...(mock.dependency || {})
        },
        additionalInfo: (mock?.additionalInfo
          ? JSON.stringify(mock.additionalInfo, null, 2)
          : null) as any,
      };

      this.formModel.opCo = this.opCoSelected as CustTpOpCo;
      // Ensure timestamp is set
      this.formModel.timestamp = mock.timestamp || new Date();

      // Set date picker to show the mock data's date
      const timestampToUse = this.formModel.timestamp instanceof Date ? this.formModel.timestamp : new Date(this.formModel.timestamp);
      this.timestampDateOverride = this.createIMyDateModel(timestampToUse);

      // Ensure premiseIds is an array
      if (!Array.isArray(this.formModel.customerInfo.premiseIds)) {
        this.formModel.customerInfo.premiseIds = [''];
      }

      // Trigger autocomplete value changes (to build options for child autocompletes)
      this.autocompleteProduct.inputElementHiddenModel.control.patchValue(this.formModel.processingContext.product);
      this.autocompleteJourney.inputElementHiddenModel.control.patchValue(this.formModel.processingContext.journey);
    }
  }

  onSubmit(form: NgForm): void {
    this.updateTimestampFromDatePicker();
    // Basic validation check (more specific checks might be needed)
    if (this.textfieldComponents) {
      let formInvalid = false;
      this.textfieldComponents
        .toArray()
        .forEach((textfieldElement: EudsTextfieldComponent) => {
          if (!textfieldElement.inputElementModel.touched) {
            // Set the textfield components as touched if they haven't been already
            textfieldElement.inputElementModel.control?.markAsTouched();
          }
          if (!textfieldElement.inputElementModel.control?.valid) {
            formInvalid = true;
          }
        });
      if (formInvalid) {
        // If any textfield component is invalid, set the form to invalid
        this.touchpointForm.form.setErrors({ valid: false });
      }
    }

    if (form.valid && !this.touchpointForm.form.hasError('valid')) {
      // Convert httpStatusCode back to number if it's a string
      if (
        this.formModel.dependency &&
        typeof this.formModel.dependency.httpStatusCode === 'string' &&
        this.formModel.dependency.httpStatusCode !== ''
      ) {
        this.formModel.dependency.httpStatusCode = parseInt(this.formModel.dependency.httpStatusCode, 10);
      } else if (this.formModel.dependency) {
        this.formModel.dependency.httpStatusCode = undefined;
      }
      // Clean empty premise IDs
      if (this.formModel.customerInfo.premiseIds) {
        this.formModel.customerInfo.premiseIds =
          this.formModel.customerInfo.premiseIds.filter((id) => id !== '');
        if (this.formModel.customerInfo.premiseIds.length === 0) {
          delete this.formModel.customerInfo.premiseIds; // Remove if empty after filtering
        }
      }
      const formData = this.deserializeForm();
      this.formSubmitted.emit({ valid: true, data: formData });
    } else {
      this.formSubmitted.emit({ valid: false, data: null });
    }
  }

  updateTimestampFromDatePicker(): void {
  // Use date override with current time if date is provided
  if (this.timestampDateOverride && this.timestampDateOverride.singleDate) {
    // Get the selected date from the IMyDateModel object
    const selectedDate = this.timestampDateOverride.singleDate.date;

    // Get current time
    const now = new Date();

    // Create new timestamp with selected date but current time
    const combinedTimestamp = new Date(
      selectedDate.year,
      selectedDate.month - 1,  // Convert from EUDS 1-indexed to JS 0-indexed
      selectedDate.day,
      now.getHours(),
      now.getMinutes(),
      now.getSeconds(),
      now.getMilliseconds()
    );

    this.formModel.timestamp = combinedTimestamp;
    } else {
      // Use current date/time
      this.formModel.timestamp = new Date();
    }
  }

  onTimestampDateChange(): void {
    this.updateTimestampFromDatePicker();
  }

  deserializeForm(): CustomerTouchPoint {
    // Create a deep copy to avoid modifying the actual formModel
    const modelCopy = JSON.parse(JSON.stringify(this.formModel));

    // Clean empty premise IDs from the copy for display
    if (modelCopy.customerInfo.premiseIds) {
      modelCopy.customerInfo.premiseIds =
        modelCopy.customerInfo.premiseIds.filter(
          (id: string) => id && id.trim() !== ''
        );
      if (modelCopy.customerInfo.premiseIds.length === 0) {
        delete modelCopy.customerInfo.premiseIds;
      }
    }
    // Remove optional fields if they are empty strings or undefined for cleaner JSON display
    Object.keys(modelCopy).forEach((key) => {
      if (modelCopy[key] === '' || modelCopy[key] === undefined) {
        // Be careful not to delete required fields even if temporarily empty
        const requiredFields = [
          'accountNumber',
          'opCo',
          'status',
          'type',
          'channel',
          'timestamp',
          'customerInfo',
        ];
        if (!requiredFields.includes(key)) {
          delete modelCopy[key];
        }
      }
    });
    if (modelCopy.customerInfo) {
      Object.keys(modelCopy.customerInfo).forEach((key) => {
        if (modelCopy.customerInfo[key] === '' || modelCopy.customerInfo[key] === undefined) {
          if (key !== 'premiseIds') {
            // Keep premiseIds field even if empty array was deleted earlier
            delete modelCopy.customerInfo[key];
          }
        }
      });
    }
    if (modelCopy.processingContext) {
      Object.keys(modelCopy.processingContext).forEach((key) => {
        if (modelCopy.processingContext[key] === ''
          || modelCopy.processingContext[key] === undefined
        ) {
          delete modelCopy.processingContext[key];
        }
      });
      if (Object.keys(modelCopy.processingContext).length === 0) {
        delete modelCopy.processingContext;
      }
    }
    if (modelCopy.dependency) {
      Object.keys(modelCopy.dependency).forEach((key) => {
        if (modelCopy.dependency[key] === ''
          || modelCopy.dependency[key] === undefined
          || modelCopy.dependency[key] === null
        ) {
          delete modelCopy.dependency[key];
        }
      });
      if (Object.keys(modelCopy.dependency).length === 0) {
        delete modelCopy.dependency;
      }
    }
    if (!modelCopy.additionalInfo || modelCopy.additionalInfo === '') {
      delete modelCopy.additionalInfo;
    }
    if (!modelCopy.processingContext?.extensionData
      || modelCopy.processingContext?.extensionData === ''
    ) {
      delete modelCopy.processingContext.extensionData;
    }

    const formData = { ...modelCopy };

    // Attempt to parse JSON fields back into objects before emitting
    try {
      if (formData.processingContext?.extensionData) {
        formData.processingContext.extensionData = JSON.parse(
          formData.processingContext.extensionData
        );
      }
    } catch (e) {
      console.warn('Could not parse processingContext.extensionData JSON', e);
      // Keep as string if parsing fails
    }
    try {
      if (formData.additionalInfo) {
        formData.additionalInfo = JSON.parse(formData.additionalInfo);
        formData.additionalInfo.generatedFromSSIP = "Generated from SSIP - " + this.authService.instance.getActiveAccount().name;
      } else {
        formData.additionalInfo = "(Generated from SSIP) " + this.authService.instance.getActiveAccount().name;
      }
    } catch (e) {
      console.warn('Could not parse additionalInfo JSON', e);
      // Keep as string if parsing fails
      formData.additionalInfo += " (Generated from SSIP) " + this.authService.instance.getActiveAccount().name;
    }

  // Add indicator if sessionId is manually set
  if (formData.sessionId && formData.sessionId.trim() !== '') {
    // Add to additionalInfo to track override usage
    if (!formData.additionalInfo) {
      formData.additionalInfo = {};
    }
    if (typeof formData.additionalInfo !== 'object' || formData.additionalInfo === null) {
      formData.additionalInfo = {};
    }
    formData.additionalInfo.sessionOverride = true;
  }


    return formData as CustomerTouchPoint;
  }

  resetForm(): void {
    const currentOpCo = this.formModel.opCo; // Preserve selected OpCo
    this.formModel = {
      accountNumber: '',
      opCo: currentOpCo, // Restore selected OpCo
      status: TouchPointStatusObject.Unknown,
      type: TouchPointTypeObject.Unknown,
      channel: InteractionChannelObject.Web,
      timestamp: new Date(),
      customerInfo: {
        phoneNumber: '',
        customerNumber: '',
        premiseIds: ['']
      },
      processingContext: {
        product: '',
        journey: '',
        step: '',
        resultCode: '',
        resultDescription: '',
        extensionData: null
      },
      dependency: {
        service: '',
        operation: '',
        endpoint: '',
        httpStatusCode: undefined
      },
      sessionId: '',
      target: '',
      additionalInfo: null
    };
    this.selectedMockData = null;

    // Reset Angular form state
    if (this.touchpointForm) {
      this.touchpointForm.resetForm(this.formModel); // Reset with default values
      // Manually set opco again after reset if needed, as resetForm might clear it
      this.formModel.opCo = currentOpCo;

      // Mark controls as pristine and untouched
      Object.keys(this.touchpointForm.controls).forEach((key) => {
        this.touchpointForm.controls[key].markAsPristine();
        this.touchpointForm.controls[key].markAsUntouched();
      });
    }
    // Explicitly reset Web Component states if resetForm doesn't cover it
    if (this.textfieldComponents) {
      this.textfieldComponents
        .toArray()
        .forEach((textfieldElement: EudsTextfieldComponent) => {
          textfieldElement.inputElementModel.control?.markAsPristine(); // Use optional chaining
          textfieldElement.inputElementModel.control?.markAsUntouched(); // Use optional chaining
          // Might need explicit value reset if ngModel binding isn't immediate after resetForm
          // textfieldElement.value = this.formModel[textfieldElement.name] or similar mapping;
        });
    }
    if (this.textareaComponents) {
      this.textareaComponents
        .toArray()
        .forEach((textareaElement: EudsTextareaComponent) => {
          textareaElement.textareaElementModel.control?.markAsPristine(); // Use optional chaining
          textareaElement.textareaElementModel.control?.markAsUntouched(); // Use optional chaining
        });
    }
    if (this.selectComponents) {
      this.selectComponents
        .toArray()
        .forEach((selectElement: EudsSelectComponent) => {
          // Reset select components if necessary
        });
    }
  }

  get prettyPrintJson(): string {
    return JSON.stringify(this.deserializeForm(), null, 2);
  }

  copyJson(): void {
    const jsonString = this.prettyPrintJson;
    navigator.clipboard
      .writeText(jsonString)
      .then(() => {
        this.showCopyFeedback('JSON copied to clipboard!', 3000); // Show success message for 3 seconds
      })
      .catch((err) => {
        console.error('Failed to copy JSON to clipboard:', err);
      });
  }

  // Helper method to show feedback and clear it after a timeout
  private showCopyFeedback(message: string, duration: number): void {
    // Clear any existing timeout
    if (this.copyMessageTimeout) {
      clearTimeout(this.copyMessageTimeout);
    }
    this.copyStatusMessage = message;
    this.cdRef.detectChanges(); // Explicitly trigger change detection

    // Set timeout to clear the message
    this.copyMessageTimeout = setTimeout(() => {
      this.copyStatusMessage = '';
      this.cdRef.detectChanges(); // Explicitly trigger change detection
    }, duration);
  }

  generateAccountNumber(): void {
    const opcoString = this.opCoSelected?.toString().toLowerCase(); // Ensure lowercase comparison
    // Determine length based on OpCo
    const length = ['ace', 'dpl', 'pepco'].includes(opcoString) ? 11 : 10;

    // Generate random digits
    let accountNumber = '';
    for (let i = 0; i < length; i++) {
      accountNumber += Math.floor(Math.random() * 10).toString();
    }

    // Update the form model
    this.formModel.accountNumber = accountNumber;
    // Manually trigger change detection if needed, though ngModel should handle it
    // this.cdRef.detectChanges();

    // Optional: Mark the control as dirty/touched if needed
    if (this.touchpointForm && this.touchpointForm.controls['accountNumber']) {
      this.touchpointForm.controls['accountNumber'].markAsDirty();
      this.touchpointForm.controls['accountNumber'].markAsTouched();
    }
  }

  // Update Account Number validation when OpCo changes
  onOpCoChange(newOpCo: OpCo) {
    this.formModel.opCo = newOpCo as CustTpOpCo;

    const opcoString = newOpCo.toString().toLowerCase();
    this.accountNumberLength = ['ace', 'dpl', 'pepco'].includes(opcoString) ? 11 : 10;
    this.accountNumberValidationError =
      `Account number for ${newOpCo} must be ${this.accountNumberLength} numeric characters`;

    // mark accountNumber field dirty so validation shows immediately
    const ctrl = this.touchpointForm.controls['accountNumber'];
    if (ctrl) {
      ctrl.markAsDirty();
      ctrl.markAsTouched();
    }
  }

  fetchProductDefinitions(): void {
    // Fetch product definitions for product, journey, and step fields
    this.apiService.getProductsAndDefinitions()
      .pipe(
        map((response: ProductWithDefinition[]) => {
          if (response && response.length > 0) {
            return response;
          } else {
            console.warn('No products data received from API.');
            return [];
          }
        }),
        finalize(() => {
          console.log('Products data retrieval complete.');
        }),
        catchError((error) => {
          console.error('Error getting products data: ', error);
          return of([]);
        })
      )
      .subscribe((data: ProductWithDefinition[]) => {
        this.productDefinitions = data;
        this.optionsProduct = Object.values(this.productDefinitions).map(product => ({ value: product.name }));
      });
  }

  productTextEntered(textEntered: any) {
    // Don't filter the options based on text entered, just show all
    this.autocompleteProduct.filteredOptions = [...this.optionsProduct];
  }

  productClearEvent(event: any) {
    // Reset product value and options when product is cleared
    this.formModel.processingContext.product = '';
    this.autocompleteProduct.filteredOptions = [...this.optionsProduct];
    // Reset journey options when product is cleared
    this.productJourneys = [];
    this.optionsJourney = [];
    // Reset step options when product is cleared
    this.journeySteps = [];
    this.optionsStep = [];
  }

  productValueChange(selectedProduct: any) {
    // Find the product by name
    let product = this.productDefinitions.find(p => p.name === selectedProduct.autocompleteValue);
    if (!product) {
      // If cannot find the product by name,
      // Find the product by alternative name
      product = this.productDefinitions.find(p => p.definition?.alternativeNames?.some(name => name === selectedProduct.inputFieldValue));
    }
    // Set the options for the journey autocomplete, or an empty array if not found
    this.productJourneys = product && product.definition && product.definition.journeys
      ? product.definition.journeys
      : [];
    if (this.productJourneys.length > 0) {
      this.optionsJourney = Object.values(this.productJourneys).map(journey => ({ value: journey.name }));
    }
    else {
      this.optionsJourney = [];
    }
  }

  journeyTextEntered(textEntered: any) {
    // Don't filter the options based on text entered, just show all
    this.autocompleteJourney.filteredOptions = [...this.optionsJourney];
  }

  journeyClearEvent(event: any) {
    // Reset journey value and options when journey is cleared
    this.formModel.processingContext.journey = '';
    this.autocompleteJourney.filteredOptions = [...this.optionsJourney];
    // Reset step options when journey is cleared
    this.journeySteps = [];
    this.optionsStep = [];
  }

  journeyValueChange(selectedJourney: any) {
    // Find the journey by name
    let journey = this.productJourneys.find(j => j.name === selectedJourney.autocompleteValue);
    if (!journey) {
      // If cannot find the journey by name,
      // Find the journey by alternative name
      journey = this.productJourneys.find(j => j.alternativeNames?.some(name => name === selectedJourney.inputFieldValue));
    }
    // Set the options for the step autocomplete, or an empty array if not found
    this.journeySteps = journey && journey.steps
      ? journey.steps
      : [];
    if (this.journeySteps.length > 0) {
      this.optionsStep = Object.values(this.journeySteps).map(step => ({ value: step.name }));
    }
    else {
      this.optionsStep = [];
    }
  }

  stepTextEntered(textEntered: any) {
    // Don't filter the options based on text entered, just show all
    this.autocompleteStep.filteredOptions = [...this.optionsStep];
  }

  stepClearEvent(event: any) {
    // Reset step value and options when step is cleared
    this.formModel.processingContext.step = '';
    this.autocompleteStep.filteredOptions = [...this.optionsStep];
  }

  stepValueChange(selectedStep: any) {
    // Step value changed
  }

// Helper method to create proper IMyDateModel object from Date
  private createIMyDateModel(date: Date): any {
    return {
      isRange: false,
      singleDate: {
        date: {
          year: date.getFullYear(),
          month: date.getMonth() + 1, // EUDS uses 1-indexed months (1-12)
          day: date.getDate()
        }
      },
      dateRange: null
    };
  }

}
